import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Switch,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import Loader from "../../../components/loader/Loader";
import { useForm } from "react-hook-form";
import EditIcon from "@mui/icons-material/Edit";
import { yupResolver } from "@hookform/resolvers/yup";
import { grey, red } from "@mui/material/colors";
import SaveIcon from "@mui/icons-material/Save";
import { dispatchSchema } from "../../../app/schema";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import {
  hideConfirmModal,
  showConfirmModal,
  showModal,
} from "../../../redux/counterSlice";
import { FormInputText } from "../../../components/form-components/FormInputText";
import { FormInputDropdown } from "../../../components/form-components/FormInputDropdown";
import ForgotPasswordModal from "./Forget-password";
import { usePermission } from "../../../hooks/usePermission";

function Dispatch() {
  const dispatch = useDispatch();
  const permissions = usePermission("Dispatch");

  const methods = useForm({
    resolver: yupResolver(dispatchSchema),
    defaultValues: {
      dispatch_name: "",
      location_details_id: "",
      password: "",
    },
  });

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = methods;

  const [loader, setLoader] = useState(true);
  const [formData, setFormData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState(null);

  const hasActionPermission =
    permissions?.edit !== false || permissions?.delete !== false;

  const loadInitialData = async () => {
    setLoader(true);
    axios
      .all([
        axios.get("/getAllDispatchDetails"),
        axios.get("/getAllActiveLocationDetails"),
      ])
      .then(function (response) {
        setFormData(response[0]?.data?.data || []);
        setLocations(response[1]?.data?.data || []);
      })
      .catch(function (err) {
        toast.error(err?.response?.data?.message || err?.message || "Failed to load data");
      })
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal({ msg: "" }));
      });
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const toggleStatus = async (id) => {
    try {
      const response = await axios.get("/dispatchSwitch/" + id);
      if (!response?.data?.error) {
        toast.success(response?.data?.message || "Status updated successfully");
        loadInitialData();
      } else {
        toast.error(response?.data?.message || "Failed to update status");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Server error");
    } finally {
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  const deleteClickHandler = async (id) => {
    try {
      if (id) {
        const response = await axios.get("/deleteDispatch/" + id);
        if (!response?.data?.error) {
          toast.success(response?.data?.message || "Dispatch deleted successfully");
          loadInitialData();
          setOpen(false);
          setSelectedRow(null);
          reset();
        } else {
          toast.error(response?.data?.message || "Failed to delete dispatch");
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Server error");
    } finally {
      dispatch(hideConfirmModal({ msg: "" }));
      setOpen(false);
    }
  };

  const editClickHandler = (item) => {
    setSelectedRow(item);
    setValue("dispatch_name", item?.dispatch_name || "");
    setValue("location_details_id", item?.location_details_id || "");
    setValue("password", "");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      setLoader(true);
      let response;

      if (selectedRow) {
        const payload = {
          dispatch_team_id: selectedRow.dispatch_team_id,
          dispatch_name: data.dispatch_name,
          location_details_id: data.location_details_id,
        };
        if (data.password && data.password.trim() !== "") {
          payload.password = data.password;
        }

        response = await axios.post("/updateDispatch", payload);
      } else {
        if (!data.password || data.password.trim() === "") {
          toast.error("Password is required for new dispatch team");
          setLoader(false);
          return;
        }

        const payload = {
          dispatch_name: data.dispatch_name,
          location_details_id: data.location_details_id,
          password: data.password,
        };

        response = await axios.post("/createDispatch", payload);
      }

      if (!response?.data?.error) {
        toast.success(
          response?.data?.message ||
            (selectedRow
              ? "Dispatch updated successfully"
              : "Dispatch created successfully")
        );
        loadInitialData();
        handleClose();
      } else {
        toast.error(response?.data?.message || "Operation failed");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Server error"
      );
    } finally {
      setLoader(false);
    }
  };

  const columns = [
    {
      name: "dispatch_team_id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <p style={{ margin: 0 }}>{+dataIndex + 1}</p>;
        },
      },
    },
    {
      name: "dispatch_name",
      label: "Dispatch Name",
    },
    {
      name: "location",
      label: "Location",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const loc = formData[dataIndex]?.location_details;
          const locObj = Array.isArray(loc) ? loc[0] : loc;
          return locObj?.location_name || "-";
        },
      },
    },
    {
      name: "region",
      label: "City / Region",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const loc = formData[dataIndex]?.location_details;
          const locObj = Array.isArray(loc) ? loc[0] : loc;
          const regionObj = Array.isArray(locObj?.region)
            ? locObj?.region[0]
            : locObj?.region;
          return regionObj?.city?.name || "-";
        },
      },
    },
    {
      name: "forget_password",
      label: "Forget Password",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const item = formData[dataIndex];
          return (
            <Button
              onClick={() => {
                setSelectedDispatch(item);
                setForgotPasswordOpen(true);
              }}
            >
              <LockResetIcon sx={{ color: red[500] }} fontSize="medium" />
            </Button>
          );
        },
      },
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const status = formData[dataIndex]?.active_status;
          return (
            <Switch
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1}
              onChange={() => {
                dispatch(
                  showConfirmModal({
                    msg: status === 1 ? "deactivate" : "activate",
                    action: () =>
                      toggleStatus(formData[dataIndex]?.dispatch_team_id),
                  })
                );
              }}
              style={{ marginBottom: "5px" }}
            />
          );
        },
      },
    },
    ...(hasActionPermission
      ? [
          {
            name: "Actions",
            options: {
              customBodyRenderLite: (dataIndex) => {
                const status = formData[dataIndex]?.active_status;
                return (
                  <div className="actionBtns">
                    {permissions?.edit !== false && (
                      <Button
                        aria-label="edit"
                        onClick={() => editClickHandler(formData[dataIndex])}
                      >
                        <EditIcon
                          fontSize="medium"
                          sx={{ color: "primary.main" }}
                        />
                      </Button>
                    )}
                    {permissions?.delete !== false && (
                      <Button
                        disabled={status === 1}
                        aria-label="delete"
                        onClick={() => {
                          dispatch(
                            showConfirmModal({
                              msg: "delete",
                              action: () =>
                                deleteClickHandler(
                                  formData[dataIndex]?.dispatch_team_id
                                ),
                            })
                          );
                        }}
                      >
                        <DeleteOutlineOutlinedIcon
                          sx={{
                            color: status === 1 ? grey[600] : red[500],
                          }}
                          fontSize="medium"
                        />
                      </Button>
                    )}
                  </div>
                );
              },
            },
          },
        ]
      : []),
  ];

  const options = {
    selectableRows: "none",
    filter: false,
    download: false,
    viewColumns: true,
    print: false,
    searchText: searchText,
    onSearchChange: (searchValue) => setSearchText(searchValue),
    enableNestedDataAccess: ".",
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Dispatch</h4>

          <MUIDataTable
            data={formData?.length > 0 ? formData : []}
            columns={columns}
            options={options}
            title={
              permissions?.add !== false ? (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSelectedRow(null);
                    reset({
                      dispatch_name: "",
                      location_details_id: "",
                      password: "",
                    });
                    setOpen(true);
                  }}
                >
                  New Dispatch
                </Button>
              ) : null
            }
          />
        </>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle className="text-primary">
          {selectedRow ? "Edit Dispatch" : "New Dispatch"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormInputText
                name="dispatch_name"
                control={control}
                label="Dispatch Name"
                errors={errors}
                mandatory="true"
              />
            </Grid>

            <Grid item xs={12}>
              <FormInputDropdown
                name="location_details_id"
                control={control}
                label="Location"
                type="single"
                options={locations?.map((item) => {
                  const reg = Array.isArray(item.region)
                    ? item.region[0]
                    : item.region;
                  const cityName = reg?.city?.name ? ` (${reg.city.name})` : "";
                  return {
                    id: item?.location_details_id,
                    name: `${item?.location_name}${cityName}`,
                  };
                })}
              />
            </Grid>

            <Grid item xs={12}>
              <FormInputText
                name="password"
                control={control}
                label={
                  selectedRow
                    ? "New Password (leave empty to keep current)"
                    : "Password"
                }
                type="password"
                errors={errors}
                mandatory={!selectedRow}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="error">
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        dispatchUser={selectedDispatch}
      />
    </>
  );
}

export default Dispatch;
